import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { normalizeTex } from "./math-text";

// Markdown + math renderer for marking answers. The lookup prompts now emit
// specialist-grade prose with real structure — bold, bullet/numbered lists, the
// occasional heading — plus LaTeX math. `MathText` renders KaTeX but NOT
// markdown, so that structure leaked as literal `**`/`-`. `RichText` runs the
// full remark→rehype pipeline (GFM + `$…$`/`$$…$$` math via remark-math →
// rehype-katex) and styles the output with Tailwind classes so we don't need
// `@tailwindcss/typography`.
//
// `normalizeTex` (shared with MathText) repairs the JSON-control-char corruption
// (`\text` → TAB+"ext", `\\vec` → doubled backslash) BEFORE markdown parsing, so
// the same LaTeX that survives in MathText survives here.

const COMPONENTS: Components = {
  p: ({ node: _n, ...p }) => <p className="mb-2 leading-relaxed last:mb-0" {...p} />,
  strong: ({ node: _n, ...p }) => <strong className="font-semibold text-ink" {...p} />,
  em: ({ node: _n, ...p }) => <em className="italic" {...p} />,
  ul: ({ node: _n, ...p }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0" {...p} />
  ),
  ol: ({ node: _n, ...p }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0" {...p} />
  ),
  li: ({ node: _n, ...p }) => <li className="leading-relaxed" {...p} />,
  h1: ({ node: _n, ...p }) => (
    <h3 className="mb-1 mt-2 text-base font-bold first:mt-0" {...p} />
  ),
  h2: ({ node: _n, ...p }) => (
    <h3 className="mb-1 mt-2 text-base font-bold first:mt-0" {...p} />
  ),
  h3: ({ node: _n, ...p }) => (
    <h4 className="mb-1 mt-2 text-sm font-bold first:mt-0" {...p} />
  ),
  a: ({ node: _n, ...p }) => (
    <a className="text-brand-600 underline" target="_blank" rel="noopener noreferrer" {...p} />
  ),
  blockquote: ({ node: _n, ...p }) => (
    <blockquote className="mb-2 border-l-2 border-black/10 pl-3 text-ink-soft" {...p} />
  ),
  code: ({ node: _n, ...p }) => (
    <code className="rounded bg-black/5 px-1 py-0.5 font-mono text-[0.9em]" {...p} />
  ),
  // Block code: own scroll + background; neutralise the nested <code> chrome.
  pre: ({ node: _n, ...p }) => (
    <pre
      className="mb-2 overflow-x-auto rounded-lg bg-black/5 p-3 font-mono text-xs [&>code]:bg-transparent [&>code]:p-0"
      {...p}
    />
  ),
  table: ({ node: _n, ...p }) => (
    <div className="mb-2 overflow-x-auto">
      <table className="w-full border-collapse text-left" {...p} />
    </div>
  ),
  th: ({ node: _n, ...p }) => (
    <th className="border-b border-black/10 px-2 py-1 font-semibold" {...p} />
  ),
  td: ({ node: _n, ...p }) => (
    <td className="border-b border-black/5 px-2 py-1 align-top" {...p} />
  ),
};

export function RichText({ text, className }: { text: string; className?: string }) {
  const src = useMemo(() => normalizeTex(text || ""), [text]);
  if (!text) return null;
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { throwOnError: false, errorColor: "#cc0000" }]]}
        components={COMPONENTS}
      >
        {src}
      </ReactMarkdown>
    </div>
  );
}

// A single-line, delimiter-free preview of an answer for tight surfaces (sidebar
// rows, hover tooltips). Strips math delimiters + markdown markers instead of
// slicing the raw string mid-`$…$` (which halved KaTeX spans and showed literal
// `$`/`**`). Heuristic — good enough for a short teaser, not for real rendering.
export function previewText(s: string): string {
  return (s || "")
    .replace(/\$\$([\s\S]*?)\$\$/g, "$1")
    .replace(/\$(?!\d)([^$\n]*?)\$/g, "$1")
    .replace(/\\\[([\s\S]*?)\\\]/g, "$1")
    .replace(/\\\(([\s\S]*?)\\\)/g, "$1")
    .replace(/\\[a-zA-Z]+\{([^{}]*)\}/g, "$1") // \text{x} → x
    .replace(/\\[a-zA-Z]+/g, "") // stray bare commands
    .replace(/[*_`#>]/g, "") // emphasis / heading / quote / code markers
    .replace(/^\s*[-•]\s+/gm, "") // bullet leaders
    .replace(/\s+/g, " ")
    .trim();
}
